import { Horizontal, Vertical, GraviGrid } from '@gravitate-js/excalibrr'
import { PlusOutlined } from '@ant-design/icons'
import { GraviButton } from '@gravitate-js/excalibrr'
import { UploadDownloadButtons } from '@components/_SiteWide/UploadStatus/UploadDownloadButtons'

import { useMemo } from 'react'
import type { SurchargeGridProps } from '../types'
import { createSurchargeColumnDefs } from '../utils'
// Import from API to avoid circular dependencies
import { useRateManagement } from '@api/useFreight/useFreight'

/**
 * Component for rendering the surcharge grid
 */
export const SurchargeGrid = ({
  surcharges,
  isLoading,
  onEdit,
  onViewRateHistory,
  gridRef,
  refreshData,
  ratebook,
  onAddSurcharge,
}: SurchargeGridProps & { onAddSurcharge: () => void }) => {
  const { exportSurcharges, importSurcharges } = useRateManagement()

  // Create column definitions with cell renderers
  const columnDefs = useMemo(() => createSurchargeColumnDefs(), [])

  // We're using direct component references in columnDefs.tsx instead of string names

  return (
    <Vertical flex={1}>
      <GraviGrid
        rowData={surcharges}
        columnDefs={columnDefs}
        externalRef={gridRef}
        loading={isLoading}
        storageKey='surcharges'
        agPropOverrides={{
          rowGroupPanelShow: 'never',
          defaultColDef: {
            sortable: true,
            resizable: true,
          },
          getRowId: (params) => params.data?._id,
          // Pass callbacks through context instead of components
          context: {
            onEdit,
            onViewRateHistory,
            ratebook,
          },
        }}
        controlBarProps={{
          title: 'Surcharges',
          actionButtons: (
            <Horizontal gap={8}>
              <UploadDownloadButtons
                fileName='Surcharges.xlsx'
                refreshCallback={refreshData}
                downloadEP={() => exportSurcharges(ratebook)}
                uploadEP={(formData: FormData) => importSurcharges(formData, ratebook)}
                downloadEPData={null}
                normalizeFunction={null}
                asOfDate={null}
                excelOptions={[
                  {
                    title: 'Download Surcharge Rates',
                    downloadEP: () => exportSurcharges(ratebook),
                    fileName: 'Surcharges.xlsx',
                  },
                  {
                    title: 'Upload Surcharge Rates',
                    uploadEP: (formData: FormData) => importSurcharges(formData, ratebook),
                  },
                ]}
              />
              <GraviButton icon={<PlusOutlined />} onClick={onAddSurcharge} buttonText='Add Surcharge' theme2 />
            </Horizontal>
          ),
        }}
      />
    </Vertical>
  )
}
